import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { green, blue } from '@material-ui/core/colors';
import CircularProgress from '@material-ui/core/CircularProgress';
import * as Mui from '@material-ui/core';
import * as MuiIcons from '@material-ui/icons';

import api from '~/services/api';
import history from '~/services/history';
import toast from '~/services/toast';
import * as monitorActions from '~/store/modules/monitor/monitor-actions';
import { getApplicationIsOnline } from '~/store/modules/app/app-selectors';
import { formatCurrency, formatDate } from '~/utils/format';
import { selectorParametroAsBoolean } from '~/store/modules/auth/auth-selectors';
import {
  API_BASE_URL,
  API_LOCAL_URL,
  API_TERMINAL_URL,
  API_VENDAS_URL,
  APP_ERROR,
  DOCUMENTO_FINALIDADE_ENUM,
  DOCUMENTO_TRANSMISSAO,
  DOCUMENTO_TRANSMISSAO_ENUM,
  MODELO_DOCUMENTO,
  TIPO_DOCUMENTO,
  STATUS_MONITOR_NFCE,
  ASSINATURA_TIPO_DOCUMENTO,
  ASSINATURA_MODAL_TIPO_OPERACAO
} from '~/constants';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    alignItems: 'center'
  },
  wrapper: {
    margin: theme.spacing(1),
    position: 'relative'
  },
  badgeOffline: {
    backgroundColor: '#ccc',
    color: 'white'
  },
  badgeOnline: {
    backgroundColor: '#4caf50',
    color: 'white'
  },
  buttonPrimary: {
    backgroundColor: blue[500],
    '&:hover': {
      backgroundColor: blue[700]
    }
  },
  buttonSuccess: {
    backgroundColor: green[500],
    '&:hover': {
      backgroundColor: green[700]
    }
  },
  fabProgress: {
    color: green[500],
    position: 'absolute',
    top: -4,
    left: -4,
    zIndex: 1
  },
  buttonProgress: {
    color: green[500],
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12
  }
}));
import Label from '~/components/Label';
import ModalVisualizarDocumento from '../../modal/ModalVisualizarDocumento';
import ModalJustificativaCancelamento from '../../modal/ModalJustificativaCancelamento';
import { Divider } from '@mui/material';
import ModalImprimirDocSaida from '~/pages/TelaVenda/modal/ModalImprimirDocSaida';
import ModalAssinatura from '~/pages/TelaVenda/modal/ModalAssinatura';
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VerifiedIcon from '@mui/icons-material/Verified';
import { selectorRotina } from '~/store/modules/auth/auth-selectors';
import ModalBoletoConvenio from '~/pages/TelaVenda/modal/ModalBoletoConvenio';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';

const DocumentosaidaTableRow = React.memo(({ data, isSelected, onRefresh, ...props }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const bloqueaReimpressao = useSelector(selectorRotina('F150DSA'));

  const isOnline = useSelector(getApplicationIsOnline());
  const { status, sendingRows } = useSelector(state => state.monitor);
  const { terminal } = useSelector(state => state.app);
  const { empresa } = useSelector(state => state?.auth);

  const parametroPC009 = useSelector(selectorParametroAsBoolean('PC009'))

  const permissaoUsuarioVerAssinatura = useSelector(selectorRotina('A040ASS')); // Veja as assinaturas dos documentos
  const permissaoUsuarioAdicionarAssinatura = useSelector(selectorRotina('A010ASS')); // Adicione assinaturas aos documentos
  const permissaoUsuarioRefazerAssinatura = useSelector(selectorRotina('A020ASS')); // Refaça as assinaturas dos documentos
  const permissaoUsuarioExcluirAssinatura = useSelector(selectorRotina('A030ASS')); // Exclua as assinaturas dos documentos

  const permissaoUsuarioCancelarDocSaida = useSelector(selectorRotina('F070DSA'));
  const [showModalImpressao, setShowModalImpressao] = useState({
    show: false,
    separaPorAmbiente: false
  });

  const [modalAssinatura, setModalAssinatura] = useState({
    open: false,
    tipoDocumento: null,
    numeroDocumento: null,
    documentoId: null,
    assinaturaSalva: null,
    somenteLeitura: false,
    assinaturaModalTipoOperacao: null,
  });

  const [showModalBoletoConvenio, setShowModalBoletoConvenio] = useState(false);
  const [cienteMulta, setCienteMulta] = useState(false);
  const [showModalJustificativaCancelamento, setShowModalJustificativaCancelamento] = useState({
    show: false,
    data: null,
    id: null
  });
  const [showModalVisualizarDocumento, setShowModalVisualizarDocumento] = useState({
    show: false,
    data: null,
    id: null
  });

  const naoPermiteCancelarDocSaida = data?.documentoCancelado

  const [anchorEl, setAnchorEl] = useState(null);

  const handleEnviarDocumento = () => {
    dispatch(monitorActions.sendDocumentoRequest(data));
  };

  const handleEditarDocumento = data => {
    setShowModalVisualizarDocumento({ show: true, data });
  };

  const handleVisualizarDocumento = data => {
    setShowModalVisualizarDocumento({ show: true, readonly: true, data });
  };

  const handleSelectOneOrder = (event, orderId) => {
    dispatch(monitorActions.selectOneRow(orderId));
  };

  const handleTransmitir = async documento => {
    dispatch(
      monitorActions.transmitirDocumento(
        { ...documento },
        {
          tentativa: 1
        },
        () => {
          dispatch(monitorActions.loadDocumentosRequest());
        }
      )
    );
  };

  const handleDownloadXml = async documento => {
    dispatch(monitorActions.downloadXml(documento));
  };

  const handleDownloadDanfe = async documento => {
    dispatch(monitorActions.downloadDanfe(documento));
  };

  const handleImprimirDocumento = async documento => {
    if (bloqueaReimpressao && documento.documentoImpresso) {
      return toast.info('Parâmetro F150DSA ativo. Sem permissão para reimpressão');
    }
    const toastInstance = toast.loading(`Carregando, aguarde...`);
    try {
      const { data: venda } = await api.get(
        documento?.id !== null
          ? `${API_BASE_URL}/v1/faturamento/documentosaida/${documento?.id}`
          : `${API_LOCAL_URL}/faturamento/documentosaida/${documento?._id}`
      );

      dispatch(
        monitorActions.printDocumento(TIPO_DOCUMENTO.DOCUMENTOSAIDA, {
          ...venda,
          isOffline: documento?.id === null
        })
      );
      dispatch(monitorActions.loadDocumentosRequest());
    } catch (error) {
      dispatch({ type: APP_ERROR, error });
    } finally {
      toastInstance.close();
    }
  };

  const handleCancelarDocumento = async documento => {
    try {
      if (documento?.documentoCancelado) {
        toast.error('Não pode cancelar documento já cancelado!');
        return;
      }

      setCienteMulta(false);
      if (
        documento?.documentoModelo === MODELO_DOCUMENTO.NFE ||
        documento?.documentoModelo === MODELO_DOCUMENTO.NFCE
      ) {
        if (documento?.documentoTransmissao === DOCUMENTO_TRANSMISSAO.NAO_TRANSMITIDO) {
          setShowModalJustificativaCancelamento({
            show: true,
            title: 'Informe o motivo da inutilização, antes de cancelar o documento.',
            callback: async justificativa => {
              const toastInstance = toast.loading(`Aguarde...`);
              try {
                const dataAtual = new Date();

                const payload = {
                  ano: dataAtual.getFullYear(),
                  uf: empresa?.uf,
                  cnpj: empresa?.cnpj,
                  modelo: documento?.documentoModelo,
                  serie: documento?.documentoSerie,
                  numeroInicial: documento?.documentoNumero,
                  numeroFinal: documento?.documentoNumero,
                  justificativa,
                  cancelarDocumento: true,
                  id: documento?.id
                };

                await api.post(
                  `${API_BASE_URL}/v1/faturamento/documentosaida/inutilizar/`,
                  payload
                );

                await cancelaDocumento(justificativa, documento);
              } catch (error) {
                dispatch({ type: APP_ERROR, error });
              } finally {
                toastInstance.close();
              }
            }
          });
        } else if (documento?.documentoTransmissao === DOCUMENTO_TRANSMISSAO.TRANSMITIDO) {
          setShowModalJustificativaCancelamento({
            show: true,
            callback: async justificativa => {
              const toastInstance = toast.loading('Cancelando, aguarde ...');
              try {
                await api.put(
                  `${API_BASE_URL}/v1/faturamento/documentosaida/transmissao/cancelamento/${documento?.id}`,
                  {
                    justificativa,
                    cienteMulta
                  }
                );

                toast.success('Cancelamento registrado na SEFAZ com sucesso!');

                await cancelaDocumento(justificativa, documento);
              } catch (error) {
                dispatch({ type: APP_ERROR, error });
              } finally {
                toastInstance.close();
              }
            }
          });
        } else {
          setShowModalJustificativaCancelamento({
            show: true,
            callback: async justificativa => {
              await cancelaDocumento(justificativa, documento);
            }
          });
        }
      } else {
        setShowModalJustificativaCancelamento({
          show: true,
          callback: async justificativa => {
            await cancelaDocumento(justificativa, documento);
          }
        });
      }
    } catch (error) {
      dispatch({ type: APP_ERROR, error });
    }
  };

  const handleImportarDocumento = () => {};

  const handleOpenMenu = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleGerarXml = async documentoId => {
    try {
      try {
        const { data: documento } = await api.get(
          `${API_BASE_URL}/V1/faturamento/documentosaida/${documentoId}`
        );

        await api.post(`${API_TERMINAL_URL}/documentosaida/gerar-xml`, {
          ...documento
        });
      } catch (error) {
        return;
      }
    } catch (error) {}
  };

  const cancelaDocumento = async (justificativa, documento) => {
    try {
      await api.put(
        `${API_VENDAS_URL}/v1/faturamento/documentosaida/cancelamento/${documento?.id}`,
        {
          cienteMulta,
          justificativa,
          emitidoWeb: true
        }
      );

      toast.success('Documento cancelado com sucesso!');

      dispatch(monitorActions.loadDocumentosRequest());
    } catch (error) {
      if (error?.response?.data?.[0]?.codigo === 'FAT0007') {
        Swal.fire({
          title: error?.response?.data?.[0]?.motivo,
          text: error?.response?.data?.[0]?.solucao,
          type: 'warning',
          buttonsStyling: false,
          showCancelButton: true,
          confirmButtonClass: 'md-button md-raised md-accent',
          cancelButtonClass: 'md-button md-raised',
          confirmButtonText: 'Confirmar',
          cancelButtonText: 'Cancelar'
        }).then(function() {
          setCienteMulta(true);
          cancelaDocumento(justificativa, documento);
        });
      } else {
        dispatch({ type: APP_ERROR, error });
      }
    }
  };

  const geraBoleto = data?.parcelas?.length > 0 && data.parcelas[0]?.finMeiopagamento?.geraBoleto;

  return (
    <>
      <Mui.TableRow hover {...props} height={48}>
        <Mui.TableCell align="center">
          {data?.id ? (
            <Mui.Tooltip title="Online">
              <Mui.Badge
                classes={{ badge: classes.badgeOnline }}
                overlap="circular"
                badgeContent={''}
              />
            </Mui.Tooltip>
          ) : (
            <Mui.Tooltip title="Offline">
              <Mui.Badge
                classes={{ badge: classes.badgeOffline }}
                overlap="circular"
                badgeContent={''}
              />
            </Mui.Tooltip>
          )}
        </Mui.TableCell>
        <Mui.TableCell>
          <Mui.Typography variant="h6">{data?.documentoModelo}</Mui.Typography>
        </Mui.TableCell>
        <Mui.TableCell>
          <Mui.Typography variant="h6">
            {data?.documentoSerie}/{data?.documentoNumero}
          </Mui.Typography>
        </Mui.TableCell>
        <Mui.TableCell>
          <Mui.Typography variant="h6">
            {formatDate(new Date(data?.documentoDataEmissao))}
          </Mui.Typography>
        </Mui.TableCell>
        <Mui.TableCell>
          <Label color="grey">{DOCUMENTO_FINALIDADE_ENUM[data?.documentoFinalidade] ?? '-'}</Label>
        </Mui.TableCell>
        <Mui.TableCell>
          <Label
            color={
              data?.documentoTransmissao === DOCUMENTO_TRANSMISSAO.TRANSMITIDO ? 'green' : 'grey'
            }
          >
            {DOCUMENTO_TRANSMISSAO_ENUM[data?.documentoTransmissao] ?? '-'}
          </Label>
        </Mui.TableCell>

        <Mui.TableCell>
          <Mui.Typography variant="h6">
            <strong>{data?.clienteNome ?? '-'}</strong>
          </Mui.Typography>
          <Mui.Typography variant="h6">{data?.clienteCpfCnpj}</Mui.Typography>
        </Mui.TableCell>
        <Mui.TableCell>
          <Mui.Typography variant="h6">{data?.fatVendedor?.nome}</Mui.Typography>
        </Mui.TableCell>
        <Mui.TableCell align="right">
          <Mui.Typography variant="h6">{formatCurrency(data?.valorTotal)}</Mui.Typography>
        </Mui.TableCell>

        <Mui.TableCell align="right" style={{ width: 140 }}>
          <Mui.Box display="flex" flexDirection="row" justifyContent="flex-end">
            {data?.isOffline && !data?.id && isOnline && (
              <div className={classes.root}>
                <div className={classes.wrapper}>
                  <Mui.IconButton
                    size="small"
                    disabled={sendingRows && sendingRows[data?._id]?.loading}
                    onClick={() => handleEnviarDocumento()}
                  >
                    <>
                      {sendingRows && sendingRows[data?._id]?.loading ? (
                        <>
                          <MuiIcons.CloudUpload />
                          <CircularProgress size={38} className={classes.fabProgress} />
                        </>
                      ) : (
                        <MuiIcons.CloudUpload />
                      )}
                    </>
                  </Mui.IconButton>
                </div>
              </div>
            )}

            <Mui.IconButton onClick={() => handleImprimirDocumento(data)}>
              <MuiIcons.Print fontSize="small" />
            </Mui.IconButton>

            <Mui.Tooltip title="Mais ações">
              <Mui.IconButton
                disabled={terminal === null}
                aria-controls="simple-menu"
                aria-haspopup="true"
                aria-label={'Mais ações'}
                onClick={handleOpenMenu}
              >
                <MuiIcons.MoreVert />
              </Mui.IconButton>
            </Mui.Tooltip>

            <Mui.Menu
              id="simple-menu"
              anchorEl={anchorEl}
              keepMounted
              open={Boolean(anchorEl)}
              onClose={handleCloseMenu}
            >
              {geraBoleto && [
                <Mui.MenuItem
                  key="gerarBoleto"
                  onClick={() => {
                    setShowModalBoletoConvenio({
                      show: true,
                      data: {
                        titulos: data?.parcelas,
                        pagamentoId: data?.parcelas?.[0]?.finMeiopagamento?.id,
                        finTitulosId: data?.parcelas?.map(titulo => titulo.id)
                      }
                    });
                  }}
                >
                  <RequestQuoteIcon fontSize="small" />
                  <span style={{ marginLeft: 12 }}> Gerar Boleto</span>
                </Mui.MenuItem>,
                <Divider style={{ marginTop: "4px", marginBottom: "4px" }} key="dividerGerarBoleto" />
              ]}

              <Mui.MenuItem
                onClick={() => {
                  if (bloqueaReimpressao && data.documentoImpresso) {
                    return toast.info('Parâmetro F150DSA ativo. Sem permissão para reimpressão');
                  } else {
                    setShowModalImpressao({ show: true, separaPorAmbiente: false });
                  }
                }}
              >
                <MuiIcons.Print fontSize="small" />{' '}
                <span style={{ marginLeft: 12 }}> Imprimir</span>
              </Mui.MenuItem>

              { parametroPC009 &&
                <Mui.MenuItem
                  onClick={() => {
                    if (bloqueaReimpressao && data.documentoImpresso) {
                      return toast.info('Parâmetro F150DSA ativo. Sem permissão para reimpressão');
                    } else {
                      setShowModalImpressao({ show: true, separaPorAmbiente: true });
                    }
                  }}
                >
                  <MuiIcons.Print fontSize="small" />{' '}
                  <span style={{ marginLeft: 12 }}> Ambiente</span>
                </Mui.MenuItem>
              }

              <Divider style={{ marginTop: "4px", marginBottom: "4px" }}/>

              {(permissaoUsuarioVerAssinatura && permissaoUsuarioAdicionarAssinatura && data?.intAssinaturaId == null) && (
                <Mui.MenuItem
                  onClick={() =>
                    setModalAssinatura({
                      open: true,
                      tipoDocumento: ASSINATURA_TIPO_DOCUMENTO.DOCUMENTOSAIDA,
                      numeroDocumento: data?.documentoNumero ?? '-',
                      documentoId: data?.id ?? null,
                      assinaturaSalva: null,
                      somenteLeitura: false,
                      assinaturaModalTipoOperacao: ASSINATURA_MODAL_TIPO_OPERACAO.ADICIONAR,
                    })
                  }
                >
                  <VerifiedIcon fontSize="small" />
                  <span style={{ marginLeft: 12 }}>
                    Assinatura
                  </span>
                  <AddIcon
                    sx={{
                      fontSize: 17,
                      mt: "-10px",
                      ml: 0.2,
                    }}
                  />
                </Mui.MenuItem>
              )}

              {(permissaoUsuarioVerAssinatura && permissaoUsuarioRefazerAssinatura && data?.intAssinaturaId != null) && (
                <Mui.MenuItem
                  onClick={() =>
                    setModalAssinatura({
                      open: true,
                      tipoDocumento: ASSINATURA_TIPO_DOCUMENTO.DOCUMENTOSAIDA,
                      numeroDocumento: data?.documentoNumero ?? '-',
                      documentoId: data?.id ?? null,
                      assinaturaSalva: data?.intAssinatura?.assinatura ?? null,
                      somenteLeitura: false,
                      assinaturaModalTipoOperacao: ASSINATURA_MODAL_TIPO_OPERACAO.EDITAR,
                    })
                  }
                >
                  <VerifiedIcon fontSize="small" />
                  <span style={{ marginLeft: 12 }}>
                    Assinatura
                  </span>
                  <EditIcon
                    sx={{
                      fontSize: 13,
                      mt: "-10px",
                      ml: 0.5,
                    }}
                  />
                </Mui.MenuItem>
              )}

              {(permissaoUsuarioVerAssinatura && permissaoUsuarioExcluirAssinatura && data?.intAssinaturaId != null) && (
                <Mui.MenuItem
                  onClick={() =>
                    setModalAssinatura({
                      open: true,
                      tipoDocumento: ASSINATURA_TIPO_DOCUMENTO.DOCUMENTOSAIDA,
                      numeroDocumento: data?.documentoNumero ?? '-',
                      documentoId: data?.id ?? null,
                      assinaturaSalva: data?.intAssinatura?.assinatura ?? null,
                      somenteLeitura: true,
                      assinaturaModalTipoOperacao: ASSINATURA_MODAL_TIPO_OPERACAO.REMOVER,
                    })
                  }
                >
                  <VerifiedIcon fontSize="small" />
                  <span style={{ marginLeft: 12 }}>
                    Assinatura
                  </span>
                  <DeleteIcon
                    sx={{
                      fontSize: 13,
                      mt: "-10px",
                      ml: 0.4,
                    }}
                  />
                </Mui.MenuItem>
              )}

              {data?.documentoModelo === MODELO_DOCUMENTO.NFCE &&
              data?.documentoTransmissao === DOCUMENTO_TRANSMISSAO.AGUARDANDO_TRANSMISSAO ? (
                <Mui.MenuItem onClick={() => handleEditarDocumento(data)}>
                  <MuiIcons.Edit fontSize="small" /> <span style={{ marginLeft: 12 }}>Editar</span>
                </Mui.MenuItem>
              ) : (
                <Mui.MenuItem onClick={() => handleVisualizarDocumento(data)}>
                  <MuiIcons.OpenInNew fontSize="small" />
                  <span style={{ marginLeft: 12 }}>Visualizar</span>
                </Mui.MenuItem>
              )}
              {(data?.documentoModelo === MODELO_DOCUMENTO.NFE ||
                data?.documentoModelo === MODELO_DOCUMENTO.NFCE) &&
              data?.nfeChave !== null &&
              data?.documentoTransmissao === DOCUMENTO_TRANSMISSAO.NAO_TRANSMITIDO ? (
                <Mui.MenuItem onClick={() => handleTransmitir(data)}>
                  <MuiIcons.CloudUpload fontSize="small" />{' '}
                  <span style={{ marginLeft: 12 }}>Transmitir</span>
                </Mui.MenuItem>
              ) : null}

              {permissaoUsuarioCancelarDocSaida && 
                <Mui.Tooltip title={(
                  naoPermiteCancelarDocSaida
                ) ? <>
                  {naoPermiteCancelarDocSaida ? 'Documento não pode ser cancelado!' : ''}
                </> : ''}>
                  <span>
                    <Mui.MenuItem disabled={naoPermiteCancelarDocSaida} onClick={() => handleCancelarDocumento(data)}>
                      <MuiIcons.Cancel fontSize="small" /> <Mui.Typography style={{ marginLeft: 12 }}>Cancelar</Mui.Typography>
                    </Mui.MenuItem>
                  </span>
                </Mui.Tooltip>
              }

              <Mui.Divider style={{ marginTop: "4px", marginBottom: "4px" }}/>
              {data?.documentoModelo === MODELO_DOCUMENTO.NFCE && data?.nfeChave !== null ? (
                <Mui.MenuItem onClick={() => handleDownloadDanfe(data)}>
                  <MuiIcons.CloudDownload fontSize="small" />{' '}
                  <span style={{ marginLeft: 12 }}>Download danfe</span>
                </Mui.MenuItem>
              ) : null}
              {data?.documentoModelo === MODELO_DOCUMENTO.NFCE && data?.nfeChave !== null ? (
                <Mui.MenuItem onClick={() => handleDownloadXml(data)}>
                  <MuiIcons.CloudDownload fontSize="small" />{' '}
                  <span style={{ marginLeft: 12 }}>Download xml</span>
                </Mui.MenuItem>
              ) : null}
            </Mui.Menu>
          </Mui.Box>
        </Mui.TableCell>
      </Mui.TableRow>

      {showModalImpressao.show && (
        <ModalImprimirDocSaida
          open={showModalImpressao.show}
          separaPorAmbiente={showModalImpressao.separaPorAmbiente}
          docSaida={data}
          onClose={() => {
            setShowModalImpressao({ show: false, separaPorAmbiente: false });
          }}
        />
      )}

      {modalAssinatura.open && (
        <ModalAssinatura
          {...modalAssinatura}
          onClose={resultado => {
              setModalAssinatura(prev => ({
                  ...prev,
                  open: false
              }));
              if (resultado && resultado.confirmado) {
                  data.intAssinaturaId = (resultado.novaAssinatura && resultado.novaAssinatura.id) ? resultado.novaAssinatura.id : null;
                  data.intAssinatura = resultado.novaAssinatura ? resultado.novaAssinatura : null;
                  setAnchorEl(null);
              }
          }}
        />
      )}

      {showModalBoletoConvenio && (
        <ModalBoletoConvenio
          showModal={showModalBoletoConvenio}
          data={{
            titulos: data?.parcelas,
            pagamentoId: data?.parcelas?.[0]?.finMeiopagamento?.id,
            finTitulosId: data?.parcelas?.map(titulo => titulo.id)
          }}
          dadoBoleto={data}
          onClose={() => {
            setShowModalBoletoConvenio(false);
          }}
        />
      )}

      {showModalVisualizarDocumento?.show && (
        <ModalVisualizarDocumento
          data={{
            documento: showModalVisualizarDocumento?.data,
            documentoId:
              showModalVisualizarDocumento?.data?.id ?? showModalVisualizarDocumento?.data?._id,
            isDocumentoOffline:
              showModalVisualizarDocumento?.data?.isOffline &&
              !showModalVisualizarDocumento?.data?.id
          }}
          readonly={showModalVisualizarDocumento?.readonly ?? false}
          showModal={showModalVisualizarDocumento?.show}
          onClose={() => {
            setShowModalVisualizarDocumento({
              show: false,
              data: { documentoId: null, isDocumentoOffline: null }
            });
          }}
        />
      )}

      {showModalJustificativaCancelamento?.show && (
        <ModalJustificativaCancelamento
          title={showModalJustificativaCancelamento?.title}
          showModal={showModalJustificativaCancelamento.show}
          data={showModalJustificativaCancelamento.data}
          onClose={() => {
            setShowModalJustificativaCancelamento({ show: false });
          }}
          callback={showModalJustificativaCancelamento?.callback}
        />
      )}
    </>
  );
});

export default DocumentosaidaTableRow;
